import { useMemo, useState } from "react"
import { useNavigate } from "react-router"
import useDeleteExperiment from "../../../api/experiments/useDeleteExperiment"
import useDuplicateExperiment from "../../../api/experiments/useDuplicateExperiment"
import { ExperimentStatus, ExperimentType } from "@prismancy/shared"
import { Experiment, Workspace } from "../../../types"
import ContextMenu from "../../elements/ContextMenu"
import ContextMenuAction from "../../elements/ContextMenuAction"
import { Modal, ModalConfirmationMessage } from "../../elements/Modal"
import useExperimentDateHelper from "../../hooks/common/useExperimentDateHelper"
import { TriggerToast } from "../../globals/Toaster"
import { isPublishedExperience, PUBLISHED_EXPERIENCE_LABELS } from "../experiment/utils/publishedExperience"
import IconFont from "../../elements/IconFont"
import { Rocket } from "lucide-react"
import { appRoutes } from "../../../routes/appRoutes"

export type ExperimentListItemType = {
  experiment: Experiment
  workspace: Workspace
}

export const ExperimentListItem: React.FC<ExperimentListItemType> = ({ experiment, workspace }: ExperimentListItemType): React.ReactElement => {

  const navigate = useNavigate()

  const duplicateExperiment = useDuplicateExperiment()
  const deleteExperiment = useDeleteExperiment()
  const { startedAt, publishedAt, endedAt, updatedAt, today } = useExperimentDateHelper(experiment, workspace)
  const isPublished = isPublishedExperience(experiment)

  const [selectedDeleteExperiment, setSelectedDeleteExperiment] = useState<Experiment | null>(null)
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false)

  const handleNavigateExperiment = (experiment: Experiment) => {
    if (isPublishedExperience(experiment)) {
      navigate(appRoutes.experiments.setup(experiment.id), { viewTransition: true })
    } else if (experiment.status === ExperimentStatus.LIVE || experiment.status === ExperimentStatus.ENDED) {
      navigate(appRoutes.experiments.reporting(experiment.id), { viewTransition: true })
    } else if (experiment.type === ExperimentType.REDIRECT) {
      navigate(appRoutes.experiments.setup(experiment.id), { viewTransition: true })
    } else {
      navigate(appRoutes.experiments.setup(experiment.id), { viewTransition: true })
    }
  }

  const handleDuplicateExperiment = async (experiment: Experiment) => {
    await duplicateExperiment({ experimentId: experiment.id })
  }

  const handleOpenDeleteModal = async (experiment: Experiment) => {
    setSelectedDeleteExperiment(experiment)
    setDeleteModalOpen(true)
  }

  const handleCloseDeleteModal = async () => {
    setSelectedDeleteExperiment(null)
    setDeleteModalOpen(false)
  }

  const handleDeleteExperiment = async () => {
    if (selectedDeleteExperiment) {
      await deleteExperiment({ experimentId: selectedDeleteExperiment.id })
    }
    setSelectedDeleteExperiment(null)
    setDeleteModalOpen(false)
  }

  const handleCopyId = () => {
    const text = experiment.id;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text).catch(() => fallbackCopy(text));
    } else {
      fallbackCopy(text);
    }
    TriggerToast('Experiment ID copied to clipboard', 2000, 'success');
  };

  const fallbackCopy = (text: string) => {
    const el = document.createElement('textarea');
    el.value = text;
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
  };

  const liveStartedAgoLabel = useMemo(() => {
    if (!startedAt?.dateKey || !today?.dateKey) return startedAt?.ago || ''

    const start = new Date(`${startedAt.dateKey}T00:00:00Z`)
    const end = new Date(`${today.dateKey}T00:00:00Z`)
    const days = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1
    const safeDays = Math.max(1, days)

    return `${safeDays} ${safeDays === 1 ? 'day' : 'days'} ago`
  }, [startedAt?.dateKey, startedAt?.ago, today?.dateKey])

  const endedAgoLabel = useMemo(() => {
    if (!endedAt?.dateKey || !today?.dateKey) return endedAt?.ago || ''

    const endDate = new Date(`${endedAt.dateKey}T00:00:00Z`)
    const currentDate = new Date(`${today.dateKey}T00:00:00Z`)
    const days = Math.floor((currentDate.getTime() - endDate.getTime()) / (1000 * 60 * 60 * 24))
    const safeDays = Math.max(0, days)

    if (safeDays === 0) return 'Today'
    return `${safeDays} ${safeDays === 1 ? 'day' : 'days'} ago`
  }, [endedAt?.dateKey, endedAt?.ago, today?.dateKey])

  const resultMetadata = useMemo(() => {
    switch (experiment.experiment_result_status) {
      case 'winner_found':
        return {
          icon: 'crown' as const,
          label: 'Winner Found',
          iconClassName: 'pi-ari-winner shrink-0',
          className: 'text-state-success',
          color: 'var(--color-text-state-success)',
        }
      case 'control_wins':
        return {
          icon: 'lightbulb' as const,
          label: 'Underperforming',
          iconClassName: 'pi-ari-underperformed shrink-0',
          className: 'text-state-error',
          color: 'var(--color-text-state-error)',
        }
      case 'inconclusive':
        return {
          icon: 'circle-question-mark' as const,
          label: 'Inconclusive',
          iconClassName: 'pi-ari-inconclusive shrink-0',
          className: 'text-body-muted',
          color: 'var(--color-text-body-muted)',
        }
      default:
        return null
    }
  }, [experiment.experiment_result_status])

  const experimentMetadata = (
    <>
      <span className="inline-flex items-center gap-[3px]">
        {experiment.type === ExperimentType.REDIRECT ? (
          <RedirectTestIcon aria-hidden="true" className="h-3 w-3 shrink-0" />
        ) : (
          <IconFont name="flask-conical" size={12} className="shrink-0 text-inherit" />
        )}
        {experiment.type === ExperimentType.REDIRECT ? 'Redirect Test' : 'A/B Test'}
      </span>
      {resultMetadata && (
        <span className="inline-flex items-center gap-[4px]" style={{ color: resultMetadata.color }}>
          <IconFont
            name={resultMetadata.icon}
            size={12}
            className={resultMetadata.iconClassName}
            style={{ color: resultMetadata.color }}
          />
          <span className={resultMetadata.className}>{resultMetadata.label}</span>
        </span>
      )}
    </>
  )

  const publishedMetadata = (
    <span className="inline-flex items-center gap-[3px]">
      <Rocket aria-hidden="true" className="h-3 w-3 shrink-0" strokeWidth={1.25} />
      {PUBLISHED_EXPERIENCE_LABELS.type}
    </span>
  )

  return (
    <>
      <span onClick={() => handleNavigateExperiment(experiment)} className="col-span-11 flex h-full min-h-5 cursor-pointer justify-start">
        <div className="flex w-full items-center justify-start">
          <span className="mr-3 block truncate text-md font-medium text-heading">{String(experiment.name).trim() || '-'}</span>
        </div>
      </span>
      <div className="col-span-1 flex h-8 items-center justify-end self-center">
        <ContextMenu>
          <ContextMenuAction onClick={() => handleNavigateExperiment(experiment)}>
            Edit
          </ContextMenuAction>
          <ContextMenuAction onClick={() => handleDuplicateExperiment(experiment)}>
            Duplicate
          </ContextMenuAction>
          <ContextMenuAction onClick={handleCopyId}>
            Copy ID
          </ContextMenuAction>
          <ContextMenuAction tone="danger" onClick={() => handleOpenDeleteModal(experiment)}>
            Delete
          </ContextMenuAction>
        </ContextMenu>
      </div>
      {
        experiment.status === ExperimentStatus.LIVE && isPublished &&
        <span
          title={`Published: ${publishedAt?.fullWithTimezone || publishedAt?.iso}`}
          onClick={() => handleNavigateExperiment(experiment)}
          className="col-span-12 mt-[4px] flex cursor-pointer items-center gap-[8px] text-xs leading-4 text-body-muted"
        >
          <span>{publishedAt?.ago || liveStartedAgoLabel}</span>
          {publishedMetadata}
        </span>
      }
      {
        experiment.status === ExperimentStatus.LIVE && !isPublished &&
        <span
          title={`Started: ${startedAt?.fullWithTimezone || startedAt?.iso}`}
          onClick={() => handleNavigateExperiment(experiment)}
          className="col-span-12 mt-[4px] flex cursor-pointer items-center gap-[8px] text-xs leading-4 text-body-muted"
        >
          <span>{liveStartedAgoLabel}</span>
          <span className="inline-flex items-center gap-[8px]">{experimentMetadata}</span>
        </span>
      }
      {
        experiment.status === ExperimentStatus.ENDED &&
        <span
          title={
            isPublished
              ? `Published: ${publishedAt?.fullWithTimezone || publishedAt?.iso}`
              : `Ended: ${endedAt?.fullWithTimezone || endedAt?.iso}`
          }
          onClick={() => handleNavigateExperiment(experiment)}
          className="col-span-12 mt-[4px] flex cursor-pointer items-center gap-[8px] text-xs leading-4 text-body-muted"
        >
          <span>{isPublished ? (publishedAt?.ago || endedAgoLabel) : endedAgoLabel}</span>
          <span className="inline-flex items-center gap-[8px]">
            {isPublished ? publishedMetadata : experimentMetadata}
          </span>
        </span>
      }
      {
        experiment.status === ExperimentStatus.DRAFT &&
        <span
          title={`Updated: ${updatedAt?.fullWithTimezone || updatedAt?.iso}`}
          onClick={() => handleNavigateExperiment(experiment)}
          className="col-span-12 mt-[4px] flex cursor-pointer items-center gap-[8px] text-xs leading-4 text-body-muted"
        >
          {updatedAt?.ago}
          <span className="inline-flex items-center gap-[8px]">{experimentMetadata}</span>
        </span>
      }
      <Modal open={deleteModalOpen} onClose={handleCloseDeleteModal} title="Delete Experiment">
        <ModalConfirmationMessage>
          Are you sure you want to delete {selectedDeleteExperiment?.name}?
        </ModalConfirmationMessage>
        <div className="mt-[16px] flex justify-between gap-[16px]">
          <button type="button" className="btn-secondary" onClick={handleCloseDeleteModal}>Cancel</button>
          <button type="button" className="btn-danger" onClick={handleDeleteExperiment}>Delete Experiment</button>
        </div>
      </Modal>
    </>
  )
}

const RedirectTestIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    width="10"
    height="9"
    viewBox="0 0 10 9"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-hidden="true"
  >
    <path
      d="M1.39149 8.00003C1.97446 8.00003 2.44705 7.52744 2.44705 6.94447C2.44705 6.3615 1.97446 5.88892 1.39149 5.88892C0.808526 5.88892 0.335938 6.3615 0.335938 6.94447C0.335938 7.52744 0.808526 8.00003 1.39149 8.00003Z"
      fill="currentColor"
    />
    <path
      d="M2.55469 6.94442H6.38802C7.12876 6.94442 7.49913 6.57405 7.49913 5.83331V1.11108"
      stroke="currentColor"
      strokeWidth={0.833333}
      strokeLinecap="round"
    />
    <path
      d="M5.83203 2.50007L7.4987 0.72229L9.16536 2.50007"
      stroke="currentColor"
      strokeWidth={0.833333}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)
